import React, { useState, useEffect } from 'react';
import './AccessibilityWidget.css';
import AccessibilityKeyboardShortcuts from './AccessibilityKeyboardShortcuts';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 100,
    contrast: 'normal',
    colorBlind: 'none',
    font: 'default',
    animations: true,
    highlightLinks: false,
    highlightHeadings: false,
    readingGuide: false,
    textToSpeech: false,
    keyboardNavigation: false
  });

  // Load saved preferences on component mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []);

  // Save preferences whenever settings change
  useEffect(() => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up reading guide
      document.removeEventListener('mousemove', readingGuideHandler);
      
      // Cancel any ongoing speech
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      
      // Remove dynamic font scaling style and attributes
      removeFontScaling();
      document.documentElement.style.removeProperty('--accessibility-font-scale');
      document.body.removeAttribute('data-font-scale');
      document.body.removeAttribute('data-accessibility-font-scaling');
    };
  }, []);

  const applySettings = (newSettings) => {
    const root = document.documentElement;
    const body = document.body;

    // Font size - Only apply scaling if different from default (100%)
    const fontScale = newSettings.fontSize / 100;
    const isScalingActive = newSettings.fontSize !== 100;
    
    if (isScalingActive) {
      root.style.setProperty('--accessibility-font-scale', fontScale);
      body.setAttribute('data-font-scale', fontScale.toString());
      body.setAttribute('data-accessibility-font-scaling', 'true');
      // Force font scaling on all elements including Material-UI components
      applyFontScaling(fontScale);
    } else {
      // Reset to default - remove scaling
      root.style.removeProperty('--accessibility-font-scale');
      body.removeAttribute('data-font-scale');
      body.removeAttribute('data-accessibility-font-scaling');
      removeFontScaling();
    }
    
    // Contrast
    body.className = body.className.replace(/accessibility-contrast-\w+/g, '');
    if (newSettings.contrast !== 'normal') {
      body.classList.add(`accessibility-contrast-${newSettings.contrast}`);
    }

    // Color blind filters
    body.className = body.className.replace(/accessibility-colorblind-\w+/g, '');
    if (newSettings.colorBlind !== 'none') {
      body.classList.add(`accessibility-colorblind-${newSettings.colorBlind}`);
    }

    // Font family
    body.className = body.className.replace(/accessibility-font-\w+/g, '');
    if (newSettings.font !== 'default') {
      body.classList.add(`accessibility-font-${newSettings.font}`);
    }

    // Animations
    if (!newSettings.animations) {
      body.classList.add('accessibility-reduce-motion');
    } else {
      body.classList.remove('accessibility-reduce-motion');
    }

    // Highlight links
    if (newSettings.highlightLinks) {
      body.classList.add('accessibility-highlight-links');
    } else {
      body.classList.remove('accessibility-highlight-links');
    }

    // Highlight headings
    if (newSettings.highlightHeadings) {
      body.classList.add('accessibility-highlight-headings');
    } else {
      body.classList.remove('accessibility-highlight-headings');
    }

    // Reading guide
    if (newSettings.readingGuide) {
      body.classList.add('accessibility-reading-guide');
      enableReadingGuide();
    } else {
      body.classList.remove('accessibility-reading-guide');
      disableReadingGuide();
    }

    // Keyboard navigation
    if (newSettings.keyboardNavigation) {
      body.classList.add('accessibility-keyboard-navigation');
    } else {
      body.classList.remove('accessibility-keyboard-navigation');
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    
    // Announce changes to screen readers
    const messages = {
      fontSize: `Font size changed to ${value}%`,
      contrast: `Contrast changed to ${value}`,
      colorBlind: `Color vision filter changed to ${value}`,
      font: `Font style changed to ${value}`,
      animations: `Animations ${value ? 'enabled' : 'disabled'}`,
      highlightLinks: `Link highlighting ${value ? 'enabled' : 'disabled'}`,
      highlightHeadings: `Heading highlighting ${value ? 'enabled' : 'disabled'}`,
      readingGuide: `Reading guide ${value ? 'enabled' : 'disabled'}`,
      keyboardNavigation: `Enhanced keyboard navigation ${value ? 'enabled' : 'disabled'}`
    };
    
    if (messages[key]) {
      announceToScreenReader(messages[key]);
    }
  };

  // Function to apply font scaling to all elements including Material-UI
  const applyFontScaling = (scale) => {
    const style = document.getElementById('accessibility-font-scaling');
    if (style) {
      style.remove();
    }
    
    const newStyle = document.createElement('style');
    newStyle.id = 'accessibility-font-scaling';
    newStyle.innerHTML = `
      /* Force font scaling on all elements */
      body * {
        font-size: calc(var(--original-font-size, 1rem) * ${scale}) !important;
      }
      
      /* Specific overrides for common patterns */
      h1, .MuiTypography-h1 { font-size: calc(2.5rem * ${scale}) !important; }
      h2, .MuiTypography-h2 { font-size: calc(2rem * ${scale}) !important; }
      h3, .MuiTypography-h3 { font-size: calc(1.75rem * ${scale}) !important; }
      h4, .MuiTypography-h4 { font-size: calc(1.5rem * ${scale}) !important; }
      h5, .MuiTypography-h5 { font-size: calc(1.25rem * ${scale}) !important; }
      h6, .MuiTypography-h6 { font-size: calc(1rem * ${scale}) !important; }
      
      /* Material-UI specific overrides */
      .MuiTypography-body1 { font-size: calc(1rem * ${scale}) !important; }
      .MuiTypography-body2 { font-size: calc(0.875rem * ${scale}) !important; }
      .MuiButton-root { font-size: calc(0.875rem * ${scale}) !important; }
      
      /* Common component overrides */
      p, span, div, a, button, input, label, li {
        font-size: calc(var(--original-font-size, 1rem) * ${scale}) !important;
      }
      
      /* Preserve original font sizes as CSS variables */
      body:not([data-font-vars-set]) * {
        --original-font-size: 1rem;
      }
    `;
    
    document.head.appendChild(newStyle);
    document.body.setAttribute('data-font-vars-set', 'true');
  };

  const removeFontScaling = () => {
    const style = document.getElementById('accessibility-font-scaling');
    if (style) {
      style.remove();
    }
    document.body.removeAttribute('data-font-vars-set');
  };

  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 100,
      contrast: 'normal',
      colorBlind: 'none',
      font: 'default',
      animations: true,
      highlightLinks: false,
      highlightHeadings: false,
      readingGuide: false,
      textToSpeech: false,
      keyboardNavigation: false
    };
    setSettings(defaultSettings);
  };

  // Store reference to avoid memory leaks
  const readingGuideHandler = (e) => {
    const readingGuide = document.querySelector('.accessibility-reading-guide-line');
    if (readingGuide) {
      readingGuide.style.top = e.clientY + 'px';
    }
  };

  const enableReadingGuide = () => {
    let readingGuide = document.querySelector('.accessibility-reading-guide-line');
    if (!readingGuide) {
      readingGuide = document.createElement('div');
      readingGuide.className = 'accessibility-reading-guide-line';
      document.body.appendChild(readingGuide);
    }

    document.addEventListener('mousemove', readingGuideHandler);
    readingGuide.style.display = 'block';
    announceToScreenReader('Reading guide enabled');
  };

  const disableReadingGuide = () => {
    const readingGuide = document.querySelector('.accessibility-reading-guide-line');
    if (readingGuide) {
      readingGuide.style.display = 'none';
    }
    document.removeEventListener('mousemove', readingGuideHandler);
    announceToScreenReader('Reading guide disabled');
  };

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      try {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.8;
        utterance.pitch = 1;
        utterance.volume = 1;
        
        // Add event listeners for better UX
        utterance.onstart = () => {
          announceToScreenReader('Started reading page content');
        };
        utterance.onend = () => {
          announceToScreenReader('Finished reading page content');
        };
        utterance.onerror = () => {
          announceToScreenReader('Error occurred while reading');
        };
        
        window.speechSynthesis.speak(utterance);
      } catch (error) {
        console.error('Speech synthesis error:', error);
        announceToScreenReader('Speech synthesis not available');
      }
    } else {
      announceToScreenReader('Text-to-speech not supported in this browser');
    }
  };

  const getMainContent = () => {
    // Try to find main content area, excluding navigation and widget
    const mainElement = document.querySelector('main, [role="main"], .main-content, #main-content');
    if (mainElement) {
      return mainElement.innerText;
    }
    
    // Fallback: get content excluding common navigation/footer elements
    const elementsToExclude = [
      '.accessibility-widget-panel',
      '.accessibility-widget-toggle', 
      'nav',
      'footer',
      '.navbar',
      '.footer',
      '[role="navigation"]',
      '[role="banner"]',
      '[role="contentinfo"]'
    ];
    
    let content = document.body.cloneNode(true);
    
    // Remove excluded elements
    elementsToExclude.forEach(selector => {
      const elements = content.querySelectorAll(selector);
      elements.forEach(el => el.remove());
    });
    
    return content.innerText;
  };

  const speakPageContent = () => {
    const content = getMainContent();
    if (content.trim()) {
      // Limit to first 2000 characters for better UX
      const textToSpeak = content.length > 2000 
        ? content.substring(0, 2000) + '... Content truncated for speech'
        : content;
      speakText(textToSpeak);
    } else {
      announceToScreenReader('No content found to read');
    }
  };

  const speakCurrentSection = () => {
    // Find the section currently in view
    const sections = document.querySelectorAll('section, article, .section, [role="region"]');
    let currentSection = null;
    
    // Find section in viewport
    for (const section of sections) {
      const rect = section.getBoundingClientRect();
      if (rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2) {
        currentSection = section;
        break;
      }
    }
    
    // Fallback to first visible heading
    if (!currentSection) {
      const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
      for (const heading of headings) {
        const rect = heading.getBoundingClientRect();
        if (rect.top >= 0 && rect.top <= window.innerHeight) {
          // Get content from heading to next heading or section
          let content = heading.textContent;
          let next = heading.nextElementSibling;
          while (next && !next.matches('h1, h2, h3, h4, h5, h6, section')) {
            content += ' ' + next.textContent;
            next = next.nextElementSibling;
            if (content.length > 1000) break; // Limit length
          }
          speakText(content);
          return;
        }
      }
    }
    
    if (currentSection) {
      const content = currentSection.textContent;
      const textToSpeak = content.length > 1500 
        ? content.substring(0, 1500) + '... Section content truncated for speech'
        : content;
      speakText(textToSpeak);
    } else {
      announceToScreenReader('No current section found to read');
    }
  };

  const announceToScreenReader = (message) => {
    const announcement = document.getElementById('accessibility-announcements');
    if (announcement) {
      announcement.textContent = message;
      // Clear after announcement
      setTimeout(() => {
        announcement.textContent = '';
      }, 1000);
    }
  };

  return (
    <>
      {/* Keyboard Shortcuts Handler */}
      <AccessibilityKeyboardShortcuts settings={settings} updateSetting={updateSetting} />
      
      {/* Accessibility Widget Button */}
      <button
        className={`accessibility-widget-toggle ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open Accessibility Menu"
        title="Accessibility Options"
      >
♿
      </button>

      {/* Live region for screen reader announcements */}
      <div 
        id="accessibility-announcements" 
        aria-live="polite" 
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      ></div>

      {/* Accessibility Widget Panel */}
      {isOpen && (
        <div className="accessibility-widget-panel" role="dialog" aria-label="Accessibility Settings">
          <div className="accessibility-widget-header">
            <h3>Accessibility Options</h3>
            <button 
              className="accessibility-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close accessibility menu"
            >
              ×
            </button>
          </div>

          <div className="accessibility-widget-content">
            {/* Text Size Controls */}
            <div className="accessibility-section">
              <h4>Text Size</h4>
              <div className="accessibility-controls">
                <button
                  onClick={() => updateSetting('fontSize', Math.max(75, settings.fontSize - 25))}
                  disabled={settings.fontSize <= 75}
                  aria-label="Decrease text size"
                >
                  A-
                </button>
                <span className="font-size-display">{settings.fontSize}%</span>
                <button
                  onClick={() => updateSetting('fontSize', Math.min(200, settings.fontSize + 25))}
                  disabled={settings.fontSize >= 200}
                  aria-label="Increase text size"
                >
                  A+
                </button>
                <button
                  onClick={() => updateSetting('fontSize', 100)}
                  aria-label="Reset text size to default"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Contrast Controls */}
            <div className="accessibility-section">
              <h4>Contrast & Colors</h4>
              <div className="accessibility-controls">
                <select
                  value={settings.contrast}
                  onChange={(e) => updateSetting('contrast', e.target.value)}
                  aria-label="Select contrast option"
                >
                  <option value="normal">Normal Contrast</option>
                  <option value="high">High Contrast</option>
                  <option value="dark">Dark Mode</option>
                  <option value="inverted">Inverted Colors</option>
                </select>
              </div>
            </div>

            {/* Color Blind Support */}
            <div className="accessibility-section">
              <h4>Color Vision</h4>
              <div className="accessibility-controls">
                <select
                  value={settings.colorBlind}
                  onChange={(e) => updateSetting('colorBlind', e.target.value)}
                  aria-label="Select color vision filter"
                >
                  <option value="none">No Filter</option>
                  <option value="protanopia">Protanopia</option>
                  <option value="deuteranopia">Deuteranopia</option>
                  <option value="tritanopia">Tritanopia</option>
                  <option value="monochrome">Monochrome</option>
                </select>
              </div>
            </div>

            {/* Font Options */}
            <div className="accessibility-section">
              <h4>Font Style</h4>
              <div className="accessibility-controls">
                <select
                  value={settings.font}
                  onChange={(e) => updateSetting('font', e.target.value)}
                  aria-label="Select font style"
                >
                  <option value="default">Default Font</option>
                  <option value="dyslexia">Dyslexia Friendly</option>
                  <option value="serif">Serif Font</option>
                  <option value="mono">Monospace Font</option>
                </select>
              </div>
            </div>

            {/* Reading Assistance */}
            <div className="accessibility-section">
              <h4>Reading Assistance</h4>
              <div className="accessibility-controls accessibility-toggles">
                <label className="accessibility-toggle">
                  <input
                    type="checkbox"
                    checked={settings.highlightLinks}
                    onChange={(e) => updateSetting('highlightLinks', e.target.checked)}
                  />
                  <span>Highlight Links</span>
                </label>
                <label className="accessibility-toggle">
                  <input
                    type="checkbox"
                    checked={settings.highlightHeadings}
                    onChange={(e) => updateSetting('highlightHeadings', e.target.checked)}
                  />
                  <span>Highlight Headings</span>
                </label>
                <label className="accessibility-toggle">
                  <input
                    type="checkbox"
                    checked={settings.readingGuide}
                    onChange={(e) => updateSetting('readingGuide', e.target.checked)}
                  />
                  <span>Reading Guide</span>
                </label>
              </div>
            </div>

            {/* Motion & Animation */}
            <div className="accessibility-section">
              <h4>Motion & Animation</h4>
              <div className="accessibility-controls accessibility-toggles">
                <label className="accessibility-toggle">
                  <input
                    type="checkbox"
                    checked={settings.animations}
                    onChange={(e) => updateSetting('animations', e.target.checked)}
                  />
                  <span>Enable Animations</span>
                </label>
              </div>
            </div>

            {/* Navigation */}
            <div className="accessibility-section">
              <h4>Navigation</h4>
              <div className="accessibility-controls accessibility-toggles">
                <label className="accessibility-toggle">
                  <input
                    type="checkbox"
                    checked={settings.keyboardNavigation}
                    onChange={(e) => updateSetting('keyboardNavigation', e.target.checked)}
                  />
                  <span>Enhanced Keyboard Navigation</span>
                </label>
              </div>
            </div>

            {/* Text-to-Speech */}
            <div className="accessibility-section">
              <h4>Text-to-Speech</h4>
              <div className="accessibility-controls">
                <button
                  onClick={speakPageContent}
                  aria-label="Read main page content aloud"
                  className="accessibility-tts-btn"
                >
                  🔊 Read Main Content
                </button>
                <button
                  onClick={() => speakCurrentSection()}
                  aria-label="Read current section aloud"
                  className="accessibility-tts-btn"
                >
                  📖 Read Current Section
                </button>
                <button
                  onClick={() => window.speechSynthesis?.cancel()}
                  aria-label="Stop speech"
                  className="accessibility-tts-btn"
                >
                  ⏹️ Stop
                </button>
              </div>
            </div>

            {/* Keyboard Shortcuts Info */}
            <div className="accessibility-section">
              <h4>Keyboard Shortcuts</h4>
              <div className="accessibility-shortcuts">
                <small>
                  <strong>Alt + A:</strong> Toggle widget<br/>
                  <strong>Alt + +/-:</strong> Font size<br/>
                  <strong>Alt + 0:</strong> Reset font<br/>
                  <strong>Alt + C:</strong> Cycle contrast<br/>
                  <strong>Alt + H:</strong> Highlight links<br/>
                  <strong>Alt + R:</strong> Reading guide<br/>
                  <strong>Alt + M:</strong> Toggle animations<br/>
                  <strong>Alt + S:</strong> Speak main content<br/>
                  <strong>Alt + Shift + S:</strong> Speak current section<br/>
                  <strong>Escape:</strong> Close widget
                </small>
              </div>
            </div>

            {/* Reset Button */}
            <div className="accessibility-section">
              <button
                onClick={resetSettings}
                className="accessibility-reset-btn"
                aria-label="Reset all accessibility settings to default"
              >
                Reset All Settings
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
